import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import playbookIcon from "../../assets/new-images/playbooks-icon.svg";
import pdfIcon from "../../assets/new-images/pdf-icon.svg";
import { runDeepInsightsPlaybook } from "../../store/apiActions";
import "./DeepInsightsPlaybooks.css";

const PLAYBOOKS = [
  {
    id: "ai-supply-chain-report",
    title: "Brazil Supply Chain Intelligence Brief",
    description: "Gives you a supply chain intelligence report for one active ingredient in Brazil.",
    useWhen:
      "you need a quick snapshot before a procurement decision, supplier negotiation, or quarterly review — covering price movement, import trends, and market signals.",
    outcome:
      "a PDF brief with China FOB pricing, Brazil import volumes, YoY comparisons, trend charts, and AI-generated market commentary with strategic recommendations.",
    steps: [
      "Fetch current China ex-works price from Baiinfo",
      "Calculate baseline price change and trend direction",
      "Pull Brazil import volumes and values YTD from EXIM Trade",
      "Compare with prior year for YoY volume change",
      "Build price trend and monthly import charts",
      "Scan Chinese market intelligence sources (CCPIA, 100PPI, AgroPages, ICAMA)",
      "Generate supply capacity outlook and strategic commentary via LLM",
      "Render the final PDF report and archive to UC Volume",
    ],
    inputPlaceholder: "e.g. GLYPHOSATE, MANCOZEB, 2,4-D",
  },
];

const createPdfUrl = (base64Content) => {
  const encodedPdf = base64Content
    .replace(/^data:application\/pdf;base64,/, "")
    .replace(/\s/g, "");
  const binaryPdf = window.atob(encodedPdf);
  const bytes = Uint8Array.from(binaryPdf, (character) => character.charCodeAt(0));

  return URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
};

const DeepInsightsPlaybooks = () => {
  const dispatch = useDispatch();
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [focus, setFocus] = useState("");
  const [runState, setRunState] = useState("idle");
  const [submittedFocus, setSubmittedFocus] = useState("");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [report, setReport] = useState(null);
  const [pdfUrl, setPdfUrl] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const elapsedTimerRef = useRef(null);
  const pdfUrlRef = useRef("");
  const requestIdRef = useRef(0);

  useEffect(
    () => () => {
      requestIdRef.current += 1;
      if (elapsedTimerRef.current) window.clearInterval(elapsedTimerRef.current);
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    },
    [],
  );

  const clearPdfUrl = () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
      pdfUrlRef.current = "";
    }
    setPdfUrl("");
  };

  const resetRun = () => {
    requestIdRef.current += 1;
    if (elapsedTimerRef.current) window.clearInterval(elapsedTimerRef.current);
    elapsedTimerRef.current = null;
    clearPdfUrl();
    setFocus("");
    setSubmittedFocus("");
    setElapsedSeconds(0);
    setReport(null);
    setErrorMessage("");
    setRunState("idle");
  };

  const openPlaybook = (playbook) => {
    resetRun();
    setSelectedPlaybook(playbook);
  };

  const closePlaybook = () => {
    resetRun();
    setSelectedPlaybook(null);
  };

  const runPlaybook = async () => {
    if (runState === "running") return;

    const activeIngredient = focus.trim() || "2,4-D";
    const startedAt = window.performance.now();
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    clearPdfUrl();
    setReport(null);
    setErrorMessage("");
    setSubmittedFocus(activeIngredient);
    setElapsedSeconds(0);
    setRunState("running");
    elapsedTimerRef.current = window.setInterval(() => {
      setElapsedSeconds((window.performance.now() - startedAt) / 1000);
    }, 100);

    try {
      const response = await dispatch(
        runDeepInsightsPlaybook(selectedPlaybook.id, {
          inputs: activeIngredient,
        }),
      );

      if (requestId !== requestIdRef.current) return;
      if (response?.error) throw response.error;
      if (!response?.artifact?.content_b64) {
        throw new Error("The playbook response did not include PDF content.");
      }

      const nextPdfUrl = createPdfUrl(response.artifact.content_b64);
      pdfUrlRef.current = nextPdfUrl;
      setPdfUrl(nextPdfUrl);
      setReport(response);
      setRunState("complete");
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      setErrorMessage(error?.message || "The report could not be generated. Please try again.");
      setRunState("error");
    } finally {
      if (requestId !== requestIdRef.current) return;
      window.clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
      setElapsedSeconds((window.performance.now() - startedAt) / 1000);
    }
  };

  const downloadPdf = () => {
    if (!pdfUrl) return;

    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = report?.artifact?.file_name || `${submittedFocus}_Supply_Chain_Brief.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  if (!selectedPlaybook) {
    return (
      <section className="deep-insights-playbooks" aria-labelledby="deep-insights-playbooks-title">
        <header className="deep-insights-playbooks-header">
          <h1 id="deep-insights-playbooks-title" className="deep-insights-playbooks-title">
            Playbooks
          </h1>
          <p className="deep-insights-playbooks-description">
            Ready-made analyses the DeepInsights team has set up for you. Open one, add a focus if you want, and run it.
          </p>
        </header>

        <div className="deep-insights-playbooks-grid">
          {PLAYBOOKS.map((playbook) => (
            <button
              type="button"
              className="deep-insights-playbook-card"
              key={playbook.id}
              onClick={() => openPlaybook(playbook)}
            >
              <span className="deep-insights-playbook-title">{playbook.title}</span>
              <span className="deep-insights-playbook-description">{playbook.description}</span>
              <span className="deep-insights-playbook-open">
                Open <span aria-hidden="true">→</span>
              </span>
            </button>
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="deep-insights-playbooks deep-insights-playbook-detail">
      <div className="deep-insights-playbook-detail-inner">
        <button type="button" className="deep-insights-playbook-back" onClick={closePlaybook}>
          <span aria-hidden="true">‹</span> All playbooks
        </button>

        <div
          className={`deep-insights-playbook-run-grid${runState !== "idle" ? " is-active" : ""}`}
        >
          <article className="deep-insights-playbook-setup">
            <h1>{selectedPlaybook.title}</h1>
            <p>{selectedPlaybook.description}</p>
            <p>
              <strong>Use it when</strong> {selectedPlaybook.useWhen}
            </p>
            <p>
              <strong>You get</strong> {selectedPlaybook.outcome}
            </p>

            <div className="deep-insights-playbook-divider" />

            <h2>Steps DeepInsights will run</h2>
            <ol className="deep-insights-playbook-steps">
              {selectedPlaybook.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>

            <div className="deep-insights-playbook-divider" />

            <label className="deep-insights-playbook-focus" htmlFor="deep-insights-playbook-focus">
              <span>
                What should it focus on? <small>(optional)</small>
              </span>
              <textarea
                id="deep-insights-playbook-focus"
                value={focus}
                onChange={(event) => setFocus(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    runPlaybook();
                  }
                }}
                placeholder={selectedPlaybook.inputPlaceholder}
                rows="3"
              />
              <small>Ctrl + Enter to run</small>
            </label>

            <button
              type="button"
              className="deep-insights-playbook-run"
              onClick={runPlaybook}
              disabled={runState === "running"}
            >
              <span className="deep-insights-playbook-run-icon" aria-hidden="true" />
              {runState === "running"
                ? "Preparing report..."
                : runState === "complete" || runState === "error"
                  ? "Run again"
                  : "Run now"}
            </button>
          </article>

          <article
            className={`deep-insights-playbook-output${
              runState === "running" ? " is-running" : runState === "complete" ? " is-complete" : ""
            }`}
            aria-live="polite"
          >
            {runState === "idle" && (
              <div className="deep-insights-playbook-output-state">
                <span className="deep-insights-playbook-output-icon">
                  <img src={playbookIcon} alt="" />
                </span>
                <h2>The result will appear here</h2>
                <p>Add a focus if you want one, then click Run now.</p>
              </div>
            )}

            {runState === "running" && (
              <div className="deep-insights-playbook-preparing">
                <div className="deep-insights-playbook-preparing-banner">
                  <span className="deep-insights-playbook-preparing-orb" aria-hidden="true" />
                  <strong>Preparing...</strong>
                  <span className="deep-insights-playbook-preparing-time">{elapsedSeconds.toFixed(1)}s</span>
                </div>
                <div className="deep-insights-playbook-skeleton" aria-hidden="true">
                  <span style={{ width: "58%" }} />
                  <span />
                  <span style={{ width: "90%" }} />
                  <span style={{ width: "72%" }} />
                  <span style={{ width: "84%" }} />
                </div>
              </div>
            )}

            {runState === "complete" && (
              <div className="deep-insights-playbook-pdf">
                <div className="deep-insights-playbook-pdf-toolbar">
                  <span className="deep-insights-playbook-pdf-title">
                    <img src={pdfIcon} alt="" />
                    <span>
                      <strong>{report?.title || "Report preview"}</strong>
                      <small>{report?.artifact?.file_name}</small>
                    </span>
                  </span>
                  <button type="button" className="deep-insights-playbook-download" onClick={downloadPdf}>
                    Download PDF
                  </button>
                </div>
                <iframe
                  className="deep-insights-playbook-pdf-frame"
                  src={`${pdfUrl}#zoom=page-width&view=FitH`}
                  title={`${submittedFocus} supply chain report`}
                />
              </div>
            )}

            {runState === "error" && (
              <div className="deep-insights-playbook-output-state" role="alert">
                <span className="deep-insights-playbook-output-icon">
                  <img src={pdfIcon} alt="" />
                </span>
                <h2>Report generation failed</h2>
                <p>{errorMessage}</p>
              </div>
            )}
          </article>
        </div>
      </div>
    </section>
  );
};

export default DeepInsightsPlaybooks;
